import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

interface DateCapacityIndicatorProps {
  maxCapacity: number;
  activeBookings: number;
  availableSpots: number;
  isClosed?: boolean;
  size?: 'small' | 'medium';
  showIcon?: boolean;
  showLabel?: boolean;
}

/**
 * Reusable capacity indicator component
 * Shows color-coded status based on availability
 *
 * Color scheme:
 * - Green: > 5 spots available
 * - Orange: 1-5 spots available (limited)
 * - Red: 0 spots (full)
 * - Gray: Closed
 */
const DateCapacityIndicator: React.FC<DateCapacityIndicatorProps> = ({
  maxCapacity,
  activeBookings,
  availableSpots,
  isClosed = false,
  size = 'small',
  showIcon = true,
  showLabel = true,
}) => {
  // Determine color and status
  const getStatus = () => {
    if (isClosed) {
      return {
        color: 'default' as const,
        label: 'Closed',
        icon: <EventBusyIcon />,
        tooltip: 'Kids Club is closed on this date',
      };
    }

    if (availableSpots === 0) {
      return {
        color: 'error' as const,
        label: showLabel ? `Full (${activeBookings}/${maxCapacity})` : `${activeBookings}/${maxCapacity}`,
        icon: <WarningIcon />,
        tooltip: `Full capacity: ${activeBookings} of ${maxCapacity} spots booked`,
      };
    }

    if (availableSpots <= 5) {
      return {
        color: 'warning' as const,
        label: showLabel ? `Limited (${availableSpots} left)` : `${activeBookings}/${maxCapacity}`,
        icon: <WarningIcon />,
        tooltip: `Limited availability: ${availableSpots} spots remaining out of ${maxCapacity}`,
      };
    }

    return {
      color: 'success' as const,
      label: showLabel ? `Available (${availableSpots} spots)` : `${activeBookings}/${maxCapacity}`,
      icon: <CheckCircleIcon />,
      tooltip: `Good availability: ${availableSpots} spots available out of ${maxCapacity}`,
    };
  };

  const status = getStatus();

  return (
    <Tooltip title={status.tooltip} arrow>
      <Chip
        icon={showIcon ? status.icon : undefined}
        label={status.label}
        color={status.color}
        size={size}
        sx={{
          fontWeight: 500,
          '& .MuiChip-icon': {
            fontSize: size === 'small' ? '0.875rem' : '1rem',
          },
        }}
      />
    </Tooltip>
  );
};

export default DateCapacityIndicator;
